import React, { useEffect, useState } from "react";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { Topic } from "../../../components/item-content-two/Topic";
import { editAccountValue, editEmailAccount, getEmailAccountById, getEmailAccountNamesWithPagination, storeEmailAccount } from "../../../services/emailAccountService";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import EmailAccountCard from "./EmailAccountCard";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { TabSummary } from "../../../models/TabSummary";
import { ExecuteCommand } from '../../../models/ExecuteCommand';
import Commands from '../../../constants/commands';
import { COMMAND_STATE } from '../../../constants/command-status';
import { AccountSubCmds } from '../../../constants/sub-commands';
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { toast } from "react-toastify";
import { EmailAccountAdvanced } from "./EmailAccountAvanced";
import { useHistory } from "react-router-dom";
import { useDialog } from "../../../components/dialog/DialogContext";
import { getErrorMessage, showError, showLoadItemError } from '../../../services/utilities/ServiceError';
import { executeCommand } from '../../../services/executeCommandService';
import { RouterRootState } from "connected-react-router";
import { useSelector } from "react-redux";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import { EmailAccountType } from "../../../models/Account";
import { findDifferencesAccountEdit } from "../../../utilities/AccountUtils";
import { MultiSelectOption } from "../../../models/MultiSelectOption";

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const initialStateCreate: EmailAccountType = {
    name: "",
    outgoingServer: { value: "", label: "" },
    outgoingPort: { value: '', label: "25" },
    incomingPort: { value: '', label: "110" },
    incomingServer: { value: "", label: "" },
    defaultAccount: false,
    enabled: 0,
    auth: { value: "", label: 'true' },
    ssl: { value: "", label: 'false' },
    address: { value: "", label: "" },
    username: { value: "", label: "" },
    password: { value: "", label: "" },
};

const allItemsPath = "/access-control/configuration/accounts";
const itemPath = "/access-control/configuration/account-setup/";

const EmailAccount = () => {
    const { translate } = useLocale();
    const dialog = useDialog();
    const history = useHistory();
    const [busy, setBusy] = useState(false);

    const initialTabs = [
        { label: translate.byKey(TranslationKeys.advanced), badge: 0, showInitial: true, isActive: true },
    ];
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<EmailAccountType>(initialStateCreate);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const reloadPage = async () => {
        try {
            let emailAccount;

            if (itemId) {
                emailAccount = await getEmailAccountById(itemId);

                setSelectedItem({ label: emailAccount.name, value: emailAccount.id.toString() })
                setCurrentItem(emailAccount);
            }
            else {
                setCurrentItem(initialStateCreate);
            }
            setTabs(initialTabs)

            setBreadcrumb([
                { value: '/access-control/configuration/', displayName: translate.byKey(TranslationKeys.configuration), disableClick: true },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.emailAccounts) },
                { value: itemPath + `${itemId}`, displayName: `${emailAccount?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    const topic: Topic = {
        accessor: "accName",
        title: translate.byKey(TranslationKeys.emailAccounts),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    };

    const testAccount = () => {
        try {
            const command: ExecuteCommand = {
                cmd: Commands.Account.label,
                cmdSubType: AccountSubCmds.Test_Account,
                entityId: itemId,
                jsonResponse: ""
            }

            setBusy(true);
            executeCommand(command)
                .then((response: any) => {
                    if (response.status === COMMAND_STATE.COMMAND_COMPLETE) {
                        return toast.success(translate.byKey(TranslationKeys.testingSettings));
                    }
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.cantOpenDoorMessage) });
                })
                .catch((error) => {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
                })
                .finally(() => {
                    setBusy(false);
                });
        } catch (error) {
            showError(error, toast.warn);
            setBusy(false);
        }
    }

    const tabContentList: ContentWrapper[] = [
        {
            content: <EmailAccountAdvanced testAccount={testAccount} />,
        },
    ];

    const getTabContent = (index?: number) => {
        const selectedTabContent = tabContentList[index ?? 0];
        return selectedTabContent.content;
    };

    const editAccount = async (values) => {
        setBusy(true);
        const differences = findDifferencesAccountEdit(currentItem, values);
        if (differences['password']) {
            differences['password']['label'] = btoa(differences['password']['label']);
        }
        const apiAccountValuePromise = buildEditApiPromises(differences, values)

        try {
            await Promise.all(apiAccountValuePromise);
            toast.success(translate.byKey(TranslationKeys.emailAccountUpdated));
            history.push(`${itemPath}${itemId}`);
            reloadPage()
        } catch (error) {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.failed),
                message: getErrorMessage(error),
            })
        } finally {

            // After save, reset password to clear it from cache. Fixes re-encoding pwd bug.
            if (values.password && values.password.label) {
                values.password.label = "";
            }

            setBusy(false);
        }
    }

    const buildEditApiPromises = (differences, values) => {
        const apiAccountValuePromise: Promise<void>[] = [];

        for (const key in differences) {
            if (differences.hasOwnProperty(key) && differences[key]["value"]) {
                const id = differences[key]["value"];
                const value = differences[key]["label"];
                const apiPromise = editAccountValue(id, value) as Promise<void>;
                apiAccountValuePromise.push(apiPromise);
            } else {
                const id = values.id;
                const value = differences;

                const apiPromise = editEmailAccount(id, value as EmailAccountType) as Promise<void>;
                apiAccountValuePromise.push(apiPromise);
            }
        }

        return apiAccountValuePromise;
    }

    const createNewAccount = async (values) => {
        values.password.label = btoa(values.password.label);

        setBusy(true);
        const item = {
            name: values.name,
            username: values.username.label,
            password: values.password.label,
            outgoingServer: values.outgoingServer.label,
            outgoingPort: values.outgoingPort.label,
            incomingServer: values.incomingServer.label,
            incomingPort: values.incomingPort.label,
            defaultAccount: values.defaultAccount,
            enabled: values.enabled ? 1 : 0,
            auth: values.auth.label.toString(),
            ssl: values.ssl.label.toString(),
            address: values.address.label,
        }

        await storeEmailAccount(item, true)
            .then(async (value) => {
                setCurrentItem(item);
                toast.success(translate.byKey(TranslationKeys.emailAccountCreated));
                history.push(`${itemPath}${value.id}`);
            })
            .catch((error) =>
                dialog.pushError({
                    title: translate.byKey(TranslationKeys.failed),
                    message: getErrorMessage(error),
                })
            )
            .finally(() => {
                // After save, reset password to clear it from cache. Fixes re-encoding pwd bug.
                values.password.label = "";
                setBusy(false);
            });
    }

    const saveEmailAccount = (values) => {
        if (itemId) {
            editAccount(values)
        }
        else {
            createNewAccount(values)
        }
    };

    return (
        <TopicContextProvider
            topic={topic}
            dataMethod={getEmailAccountNamesWithPagination}
            itemId={itemId}
            selectedItem={selectedItem}
        >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => { history.push(allItemsPath) }}
                    saveDetails={saveEmailAccount}
                    tabs={tabs}
                    itemPath={itemPath}
                    initialValues={currentItem}
                >
                    <EmailAccountCard />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
    );
};

export default EmailAccount;